import { Atom } from 'BMapsModel';
import { isPlanar, pointsCentroid, ringNormalVector } from 'BMapsUtil/atom_distance_utils';
import AmberData from 'BMapsSrc/chemistry/AmberData';
import { getFullResidueId } from 'BMapsUtil/mol_info_utils';
import {
    DegreesFromRadians, Distance, Dot3, Length3,
} from 'BMapsSrc/math';
import { lookupRingInfo } from 'BMapsSrc/atomnames';

/**
 * @typedef {{ planarDotTolerance: number, debug: boolean }} RingOptions
 * @typedef {import('BMapsModel').AtomGroup} AtomGroup
 */

export const DefaultRingOptions = Object.freeze({
    planarDotTolerance: null,
    debug: false,
});

export class Ring {
    /**
     * Create Ring objects, also assigning fused rings
     * @param {AtomGroup} ag
     * @returns {Ring[]}
     */
    static ringsForAtomGroup(ag) {
        const ringInfoMap = Atom.ringInfoForAtoms(ag.getAtoms());

        // First create all the rings, then add fusions with the ring objects (not just ordinals)
        const newRings = Object.entries(ringInfoMap).reduce(
            (acc, [ringOrd, rInfo]) => ({ ...acc, [ringOrd]: new Ring(ag, rInfo.atoms, ringOrd) }),
            {}
        );

        for (const [ringOrd1, rInfo] of Object.entries(ringInfoMap)) {
            const ring = newRings[ringOrd1];
            for (const ringOrd2 of rInfo.fusions) {
                ring.addFusedRing(newRings[ringOrd2]);
            }
        }
        return Object.values(newRings);
    }

    /**
     *
     * @param {AtomGroup[]} ags
     * @returns {Ring[]}
     */
    static ringsForAtomGroups(ags) {
        const rings = ags.flatMap((ag) => Ring.ringsForAtomGroup(ag));
        return rings;
    }

    /**
     *
     * @param {AtomGroup} atomGroup
     * @param {Atom[]} atoms
     * @param {number} ringOrdinal
     */
    constructor(atomGroup, atoms, ringOrdinal) {
        this.atomGroup = atomGroup;
        this.atoms = atoms;
        this.fusedRings = [];
        this.ringOrdinal = Number(ringOrdinal);
        this.resSpec = getFullResidueId(atoms[0]);
        this.centroid = pointsCentroid(atoms);
        this.normal = ringNormalVector(atoms);
    }

    toString() {
        return `Ring ${this.resSpec} ${this.ringOrdinal}: ${this.atoms.map((a) => a.atom).join(' ')}`;
    }

    hasAtom(atom) {
        return this.atoms.includes(atom);
    }

    isMergedRing() {
        // Merged rings have ordinals >= 1000
        return this.ringOrdinal >= 1000;
    }

    addFusedRing(ring) {
        const sharedAtoms = this.atoms.filter((atom) => ring.hasAtom(atom));
        this.fusedRings.push({ ring, sharedAtoms });
    }

    /**
     * Return aromatic rings, but replace fused aromatic rings with a single ring.
     * @param {Ring[]} rings
     * @param {RingOptions} ringOptions
     * @returns {Ring[]}
     */
    static combineAromaticRings(rings, ringOptions) {
        const resultRings = [];
        const fusions = [];
        const aromaticRings = filterAromaticRings(rings, ringOptions);
        for (const ring of aromaticRings) {
            const fusedRings = ring.fusedRings.filter(({ ring: r }) => aromaticRings.includes(r));
            if (fusedRings.length === 0) {
                resultRings.push(ring);
                continue;
            }
            const neighbors = [ring, ...fusedRings.map(({ ring: r }) => r)];
            // Collects all fused aromatic rings into a single set
            let fusion = fusions.find((f) => neighbors.some((r) => f.has(r)));
            if (!fusion) {
                fusion = new Set(neighbors);
                fusions.push(fusion);
            }
            neighbors.forEach((r) => fusion.add(r));
        }

        for (const [i, fusion] of fusions.entries()) {
            const fusedRings = [...fusion];
            if (fusedRings.length > 2) {
                console.log(`getFusedAromaticRings: fusing ${fusedRings.length} rings into one: ${fusedRings.map((r) => r.toString()).join(', ')}`);
            }
            const atoms = fusedRings.flatMap((r) => r.atoms);
            const ordinal = 1000+i; // make up something to be clear it's synthetic
            resultRings.push(new Ring(fusedRings[0].atomGroup, atoms, ordinal));
        }
        return resultRings;
    }
}

/**
 * @param {Ring} ring1
 * @param {Ring} ring2
 * @returns {number}
 */
export function ringDistance(ring1, ring2) {
    return Distance(ring1.centroid, ring2.centroid);
}

/**
 * @param {Ring[]} inputRings
 * @param {RingOptions} options
 * @returns {Ring[]}
 */
export function filterAromaticRings(inputRings, options=DefaultRingOptions) {
    return inputRings.filter((ring) => isAromaticRing(ring.atoms, options));
}

/**
 * @param {Atom[]} atoms
 * @param {PiPiParams} options
 */
export function isAromaticRing(atoms, options=DefaultRingOptions) {
    const desc = `${getFullResidueId(atoms[0])} - ${describeRingAtoms(atoms, true)}`;
    if (!atoms[0].hetflag) {
        // Amino/Nucleic Acid case: use builtin ringInfo lookup
        let found = lookupRingInfo(atoms[0].resn); // { [ringOrdinal]: ringInfo, ... }
        if (found) {
            const aromaticRings = Object.values(found).filter((rInfo) => rInfo.aromatic);
            const fusedAromaticRingAtomNames = aromaticRings.flatMap((rInfo) => rInfo.atoms);
            // Find the actual ringInfo for this group of atoms or allow fused ring situations.
            // Amino/nucleic acids have maximum of 2 fused aromatic rings.
            found = aromaticRings.find((rInfo) => matchingAtomNames(atoms, rInfo.atoms))
                || matchingAtomNames(atoms, fusedAromaticRingAtomNames);
        }
        if (!found) {
            if (options.debug) console.log(`isAromaticRing - rejecting ${desc} because not aromatic ring.`);
            return false;
        }
    } else {
        // Ligand/Compound case: check for aromatic amber types
        // Some back and forth on the heuristic because we need to handle cases of being
        // connected to multiple rings and other parts of the compound.
        // We considered:
        // "at least one aromatic atom" but that admits non-aromatic rings fused to aromatic rings
        // "all atoms are aromatic" but doesn't admit builtin projects with suspect na amber types
        // "at least one aromatic atom with no external bonds"
        // We settled on "at least one aromatic atom with only one ring ordinal"
        const gaffAromaticTypes = AmberData.GaffAromaticTypes;
        if (!atoms.some(
            (at) => gaffAromaticTypes.includes(at.amber) && at.getRingOrdinals().length === 1
        )) {
            if (options.debug) console.log(`iSAromaticRing - rejecting ${desc} because not aromatic ring.`);
            return false;
        }
    }

    // Planar check is disabled by default, but allow it to be specified.
    const tolerance = options.planarDotTolerance ?? DefaultRingOptions.planarDotTolerance;
    if (tolerance != null && !isPlanar(atoms, { tolerance, debug: options.debug })) {
        if (options.debug) console.log(`isAromaticRing - rejecting ${desc} because not planar (tolerance ${tolerance})`);
        return false;
    }
    return true;
}

function describeRingAtoms(atoms, includeAmber=false) {
    return atoms.map((a) => describeRingAtom(a, includeAmber)).join(', ');
}

function describeRingAtom(atom, includeAmber=false) {
    return includeAmber ? `${atom.atom} (${atom.amber})` : atom.atom;
}

/**
 * @param {Atom[] | string[]} a
 * @param {Atom[] | string[]} b
 * @returns {boolean}
 */
function matchingAtomNames(a, b) {
    const atomNames1 = a.map((atom) => (typeof atom === 'string' ? atom : atom.atom));
    const atomNames2 = b.map((atom) => (typeof atom === 'string' ? atom : atom.atom));
    return atomNames1.length === atomNames2.length
        && atomNames1.every((name) => atomNames2.includes(name));
}

/**
 * Return the angle for the intersection of the planes of two rings.
 * @param {Ring} ring1
 * @param {Ring} ring2
 * @returns {number}
 */
export function getRingsPlaneAngle(ring1, ring2) {
    const normal1 = ring1.normal;
    const normal2 = ring2.normal;
    const dot = Dot3(normal1, normal2);
    const normalLength1 = Length3(normal1);
    const normalLength2 = Length3(normal2);
    let angle = Math.acos(dot / (normalLength1 * normalLength2));
    angle = DegreesFromRadians(angle);
    if (angle > 90) angle = 180 - angle;
    return angle;
}
